import { Injectable, Logger, Optional } from '@nestjs/common';
import * as nodemailer from 'nodemailer';
import { PrismaService } from '@cart/api-data-access';
import { TemplateRenderService } from './template-render.service';

export interface EmailAttachment {
  filename: string;
  content: Buffer;
  contentType: string;
}

export interface SendEmailInput {
  to: string;
  subject: string;
  html: string;
  /**
   * Files to attach. Kept out of the EmailLog row deliberately — a log that
   * stores a copy of every invoice PDF would grow without bound and duplicate
   * a document that already has one authoritative home in R2.
   */
  attachments?: EmailAttachment[];
  // Which TemplateType this came from, if any — carried through purely so
  // the EmailLog row can be filtered/grouped by template later; send()
  // itself doesn't use it for anything.
  typeCode?: string;
}

// Fail-soft like AuditService — a missing/misconfigured provider must never
// turn a request (e.g. forgot-password) into a hard error, since that would
// leak information via response-timing/error differences. Callers should
// treat this service as best-effort and never await it for correctness.
@Injectable()
export class EmailService {
  private readonly logger = new Logger(EmailService.name);
  private readonly transporter: nodemailer.Transporter | undefined;

  constructor(
    @Optional() private readonly templateRender?: TemplateRenderService,
    @Optional() private readonly prisma?: PrismaService
  ) {
    const { SMTP_HOST, SMTP_PORT, SMTP_USER, SMTP_PASS } = process.env;

    this.transporter =
      SMTP_HOST && SMTP_USER && SMTP_PASS
        ? nodemailer.createTransport({
            host: SMTP_HOST,
            // 465 is implicit TLS, 587/25 use STARTTLS — nodemailer needs
            // `secure` set explicitly per port rather than inferring it.
            port: Number(SMTP_PORT ?? 465),
            secure: Number(SMTP_PORT ?? 465) === 465,
            auth: { user: SMTP_USER, pass: SMTP_PASS },
          })
        : undefined;

    if (!this.transporter) {
      // Loud on purpose — this is the exact gap that let a merchant
      // registration return 201 with no visible sign the verification
      // email was never even attempted. Fires once at boot instead of only
      // showing up the first time a real user action needs an email sent.
      this.logger.warn(
        'SMTP not configured (SMTP_HOST/SMTP_USER/SMTP_PASS must all be set) — emails will be skipped, not sent.'
      );
    } else if (process.env.NODE_ENV !== 'test') {
      // createTransport() never touches the network — a wrong host/port/
      // credential wouldn't surface until the first real send silently
      // failed. Verifying now (fire-and-forget, doesn't block boot) gets
      // that failure into the startup logs immediately instead of only
      // as a 'failed' EmailLog row after a real user is already affected.
      // Skipped under the test runner — a unit test constructing a real
      // EmailService shouldn't make a live network call to whatever mail
      // server this machine's real .env happens to point at.
      this.transporter.verify().then(
        () => this.logger.log(`SMTP verified: connected to ${SMTP_HOST}:${SMTP_PORT ?? 465} as ${SMTP_USER}`),
        (error) =>
          this.logger.error(
            `SMTP verification failed for ${SMTP_HOST}:${SMTP_PORT ?? 465} as ${SMTP_USER} — ${(error as Error).message}`
          )
      );
    }
  }

  async send(input: SendEmailInput): Promise<void> {
    if (!this.transporter) {
      this.logger.error(`SMTP not configured — skipping email to ${input.to}`);
      await this.logSend(input, 'failed', 'SMTP not configured (SMTP_HOST/SMTP_USER/SMTP_PASS missing)');
      return;
    }

    const { from, replyTo } = await this.resolveSenderIdentity();

    try {
      const info = await this.transporter.sendMail({
        from,
        to: input.to,
        subject: input.subject,
        html: input.html,
        ...(input.attachments?.length ? { attachments: input.attachments } : {}),
        ...(replyTo ? { replyTo } : {}),
      });
      await this.logSend(input, 'sent', undefined, info?.messageId);
    } catch (error) {
      const message = (error as Error).message;
      this.logger.error(`Failed to send email to ${input.to}: ${message}`);
      await this.logSend(input, 'failed', message);
    }
  }

  // Logging is itself fail-soft — a broken EmailLog write must never mask
  // (or throw over) the real send outcome above it.
  private async logSend(
    input: SendEmailInput,
    status: 'sent' | 'failed',
    errorMessage?: string,
    messageId?: string
  ): Promise<void> {
    try {
      await this.prisma?.emailLog.create({
        data: {
          typeCode: input.typeCode,
          recipient: input.to,
          subject: input.subject,
          status,
          errorMessage,
          messageId,
        },
      });
    } catch (error) {
      this.logger.warn(`Failed to write EmailLog for ${input.to}: ${(error as Error).message}`);
    }
  }

  async sendPasswordResetEmail(to: string, resetUrl: string): Promise<void> {
    if (process.env.NODE_ENV !== 'production') {
      this.logger.log(`[dev] Password reset link for ${to}: ${resetUrl}`);
    }

    await this.sendTemplated('password_reset', to, { resetUrl });
  }

  // Shared entry point for every DB-backed template trigger — loads +
  // renders via TemplateRenderService, then sends. A missing/inactive
  // template or a missing TemplateRenderService (e.g. in a unit test
  // constructing EmailService directly) both fail soft: log and skip,
  // never throw into the caller's business-logic flow.
  async sendTemplated(
    typeCode: string,
    to: string,
    variables: Record<string, string>,
    attachments?: EmailAttachment[]
  ): Promise<void> {
    const rendered = await this.templateRender?.render(typeCode, variables);

    if (!rendered) {
      // Same blind spot the "SMTP not configured" fix closed above, one
      // stage earlier in the pipeline: this used to skip with only a
      // console warning and no EmailLog row — so one specific template
      // (e.g. email_verification) missing its Active+Published row on a
      // given environment, while every other template is fine, was
      // invisible anywhere queryable. Now it leaves the same failed trail.
      this.logger.error(
        `Skipping "${typeCode}" email to ${to} — no active/published template found for this type`
      );
      await this.logSend(
        { to, subject: `(unrendered: ${typeCode})`, html: '', typeCode },
        'failed',
        `No active/published template found for type "${typeCode}"`
      );
      return;
    }

    await this.send({ to, subject: rendered.subject, html: rendered.html, typeCode, attachments });
  }

  // DB-configured sender identity (Settings page) wins when set; env vars
  // are the fallback so this still works with zero DB configuration, same
  // fail-soft posture as everything else here.
  private async resolveSenderIdentity(): Promise<{ from: string; replyTo?: string }> {
    const envFrom = process.env.EMAIL_FROM ?? 'noreply@forgestack.in';

    try {
      const settings = await this.prisma?.emailSettings.findFirst();
      const from = settings?.senderEmail
        ? settings.senderName
          ? `${settings.senderName} <${settings.senderEmail}>`
          : settings.senderEmail
        : envFrom;

      return { from, replyTo: settings?.replyToEmail ?? undefined };
    } catch {
      return { from: envFrom };
    }
  }
}
